<script>
   /*************************************************************/
   /* AppContainer for prediction                               */
   /* -------------------------                                 */
   /* app container for applying PCA model                      */
   /*************************************************************/

   import { downloadResults } from '../utils';

   import ModelLoader from './ModelLoader.svelte';
   import AppPlots from './AppPlots.svelte';

   import DataLoader from '../../../shared/components/PCADataLoader.svelte';
   import ResultTable from '../shared/ResultTable.svelte';

   import Outliers from '../../../shared/widgets/Outliers.svelte';
   import Container from '../../../shared/controls/Container.svelte';
   import Button from '../../../shared/controls/Button.svelte';


   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   // inputs
   export let WASM;              // WASM promis method from parent app


   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // main properties of the training app
   // all set to undefined to avoid triggering
   // reset conditions on first render

   let dataset;               // new set
   let model;                 // object with PCA model
   let res;                   // object with PCA results for calibration set

   // parameters to check if model object has been updated
   let modelHash = 0;

   // array with outliers
   let outliers = [];         // vector with dataset row numbers (from 0) marked as outliers


   /**
    * Set the selected point as outlier and reset the model and results
    * @param e - event dispatched by AppPlots component with id of the selected point
    */
   function setOutlier(e) {

      // get position (index) of the sample to be removed
      const pos = parseInt(e.detail.id);
      if (Number.isNaN(pos) || pos < 0 || pos >= res.objindices.length ) return;

      // find sample ID corresponding to the position
      const id = res.objindices[pos];

      // filter the outlier out
      if (id >= 0 && !outliers.includes(id)) {
         outliers = [...outliers, id];
      }
   }


   /**
    * Reset outliers and model object
    */
   function resetOutliers() {
      outliers = [];
   }

   // if model is null - reset dataset
   $: if (model === null) {
      dataset = null;
      modelHash = 0;
   }


   // if dataset is null - reset all dependent objects (results, labels, outliers, etc.)
   $: if (dataset === null) {
      res = null;
      outliers = [];
   }

   // check hashes and initiate updates if outcomes changed
   $: if (model && model.hash !== modelHash) {
      modelHash = model.hash;
   }
</script>

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="model-loader">
         <ModelLoader bind:model={model} />
      </div>

      {#if model}
      <div class="data-loader">
         <DataLoader bind:dataset={dataset} bind:res={res} {WASM} {model} {outliers} className="Group" />
      </div>
      {/if}

      {#if outliers.length > 0 }
      <div class="outliers-pane">
         <Outliers {dataset} on:resetoutliers={resetOutliers} bind:outliers={outliers} />
      </div>
      {/if}

      {#if res }
      <div class="model-dowloader">
         <Container label='Download'>
            <div class="buttons">
            <Button on:click={() => downloadResults(res, dataset, "new", outliers)}>Results</Button>
            </div>
         </Container>
      </div>
      <div class="result-stats">
         <ResultTable {res} />
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !res}
         <div class="help-text">
            <p>
               In this tab you can project new data points to a previosly trained PCA model and explore main outcomes: scores, distances and variances.
            </p>
            {#if !model}
            <p><strong>Step 1.</strong></p>
            <p>Load file with PCA model you created earlier in tab <strong>Train</strong></p>
            {:else}
            <p><strong>Step 2.</strong></p>
            <p>Load your dataset set from a CSV file. The interface and selection steps are similar to the ones you used in <em>Train</em> tab. However, in this case you do not need to specify all details for header, just select if the new dataset has header as a first row or not. All values will be inherited from the model object.
            </p>
            <p><strong>It is important that the new dataset has same variables as the training set used to create the model.</strong></p>
            {/if}
            <p></p>
         </div>
      {:else}
         {#key res }
         <AppPlots on:setoutlier={setOutlier} {dataset} {res} {model} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0 1em 0 0.5em;
      padding: 0.25em 0.25em;
      height: 100%;
   }

   .app-controls > div {
      background: #e8e8e8;
      border-radius: 0.25rem;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p, .help-text > div {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text table {
      border-collapse: collapse;
   }

   .help-text table td{
      padding: 0.25em 0.5em;
      background: #e0f0fa;
      text-align: right;
      border: 1px solid white;
   }

   .help-text table tr:first-child td{
      font-weight: bold;
      background: #f8f4e0;
   }

   .help-text table td:first-child {
      font-weight: bold;
      background: #f8f4e0;
      padding-right: 1em;
   }

   .help-text table td:nth-child(2) {
      background: #e0e8f0;
      padding-right: 1em;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   .result-stats {
      padding: 0.5rem 1rem;
      margin-top: 0.25rem;
      font-size: 0.85em;
   }

   .buttons {
      position: relative;
      box-sizing: border-box;
      display: inline-block;
      flex: 1 1 auto;
      margin: 0;
      padding: 2px 0;
      width: auto;
      border-radius: 2px;
      cursor: default;
      white-space: nowrap;
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(320px, 30%) 1fr;
      }
   }

</style>